import type { ApiError as ApiErrorBody } from '@tex/finance-shared'

export const API_URL = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    /** Machine-readable `error` code from the API body, or `http_${status}` when the body had none. */
    public readonly code: string,
    public readonly reason?: string,
  ) {
    super(reason ? `${code}: ${reason}` : code)
    this.name = 'ApiError'
  }
}

/** Only same-origin absolute paths are accepted as a post-sign-in destination. */
export function isLocalPath(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')
}

/**
 * Where the API sends a caller whose session is too old for a privileged action
 * (`403 step_up_required`). Re-authenticating creates a fresh session; `redirect` brings
 * the user back to the page they were on.
 */
export function stepUpPath(returnTo: string = `${window.location.pathname}${window.location.search}`): string {
  return `/sign-in?step_up=1&redirect=${encodeURIComponent(returnTo)}`
}

function retryCopy(res: Response, body: Partial<ApiErrorBody> & { retry_after_seconds?: number }): string {
  const seconds = body.retry_after_seconds ?? Number(res.headers.get('retry-after'))
  return Number.isFinite(seconds) && seconds > 0
    ? `Too many requests. Try again in ${seconds} second${seconds === 1 ? '' : 's'}.`
    : 'Too many requests. Wait a moment and try again.'
}

async function readError(res: Response, path: string): Promise<ApiError> {
  let body: Partial<ApiErrorBody> & { retry_after_seconds?: number } = {}
  try {
    body = (await res.json()) as typeof body
  } catch {
    // non-JSON error body: fall through to the status-only error
  }
  if (res.status === 429) return new ApiError(429, 'rate_limited', retryCopy(res, body))
  if (res.status === 403 && body.error === 'step_up_required') {
    window.location.assign(stepUpPath())
    return new ApiError(403, 'step_up_required', 'Confirm it is you to continue: signing in again…')
  }
  return new ApiError(res.status, body.error ?? `http_${res.status}`, body.reason ?? `${path} failed with ${res.status}`)
}

export async function apiGet<T>(path: string, headers: Record<string, string> = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', headers })
  if (!res.ok) throw await readError(res, path)
  return (await res.json()) as T
}

export async function apiSend<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? headers : { 'content-type': 'application/json', ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) throw await readError(res, path)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError && (err.code === 'rate_limited' || err.code === 'step_up_required')) return err.reason ?? err.code
  if (err instanceof ApiError) return err.reason && err.reason !== err.code ? `${err.code} — ${err.reason}` : err.code
  if (err instanceof Error) return err.message
  return 'Something went wrong'
}
